import { useEffect, useRef, useState, type ReactNode } from 'react';
import { makeStyles, Spinner } from '@fluentui/react-components';
import { formatEta } from './TrabajosEnCurso.js';

/**
 * Progreso en presupuesto (jul 2026).
 *
 * Armar hoja (extract): barra SIEMPRE en movimiento (indeterminada).
 * Color por etapa con tokens del design system (claro/oscuro):
 *   1. accent — empezando (azul marca)
 *   2. accent-strong — leyendo
 *   3. ok — cerrando (verde éxito, no “semáforo de alarma”)
 * Evitamos rojo/ámbar: el constructor los lee como error/aviso.
 *
 * Equivalentes (pricing): barra ligada a procesados/total, color accent.
 */

const STAGE_COLORS = {
  1: 'var(--accent)',
  2: 'var(--accent-strong)',
  3: 'var(--ok)',
} as const;

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '14px 16px',
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    minWidth: 0,
    width: '100%',
  },
  textCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1 1 0%',
    minWidth: 0,
    overflow: 'hidden',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 'var(--fs-300)',
    color: 'var(--text-primary)',
  },
  sub: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  count: {
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: 'var(--fs-300)',
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    minWidth: 0,
  },
  stagePill: {
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.02em',
    padding: '2px 8px',
    borderRadius: '999px',
    color: '#fff',
  },
  barTrack: {
    height: '8px',
    borderRadius: '999px',
    background: 'var(--bg-overlay)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    // Contiene cualquier pintura hija; evita scroll horizontal en el padre.
    contain: 'paint',
  },
  barFill: {
    display: 'block',
    height: '100%',
    borderRadius: '999px',
    transitionProperty: 'width, background-color',
    transitionDuration: '350ms',
  },
  /**
   * Indeterminada en movimiento continuo.
   * Animamos background-position (no translateX): no ensancha el scroll de la página.
   */
  barTrackMoving: {
    height: '8px',
    borderRadius: '999px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    contain: 'paint',
    backgroundColor: 'var(--bg-overlay)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '36% 100%',
    animationName: {
      '0%': { backgroundPosition: '-36% 0' },
      '100%': { backgroundPosition: '136% 0' },
    },
    animationDuration: '1.25s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  metaRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
  },
  metaNum: {
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },
  eta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    marginLeft: 'auto',
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  latido: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
  },
  latidoPunto: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
    background: 'var(--accent)',
    animationName: { '0%,100%': { opacity: 0.25 }, '50%': { opacity: 1 } },
    animationDuration: '1.4s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  /** Algo no está saliendo como debería: se dice, no se disimula. */
  aviso: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: 'var(--fs-100)',
    color: 'var(--warn)',
  },
  /** Fila de espera: la barra existe pero NO se mueve — nada está avanzando. */
  barTrackQuieto: {
    height: '8px',
    borderRadius: '999px',
    background: 'var(--bg-overlay)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
});

export interface QuoteBuildingAnimationProps {
  stage: string;
  stageLabel: string;
  message?: string | null;
  pct?: number;
  processed?: number;
  total?: number;
  priced?: number;
  etaMs: number | null;
  extra?: ReactNode;
  /**
   * El trabajo espera su turno: NADIE lo está procesando todavía. Es un estado
   * distinto de "leyendo el documento" y se cuenta como lo que es — antes caía en
   * la rama de lectura y la pantalla decía "Preparando la lectura de tu documento…"
   * con nadie del otro lado (reporte de Sebastián, 2026-08-12).
   */
  enCola?: boolean;
  /** Desde cuándo espera en la fila (epoch ms). Alimenta el reloj de la espera. */
  esperandoDesde?: number | null;
  /**
   * Cuándo llegó el último aviso del proceso (epoch ms); null = nunca llegó ninguno.
   * Es la ÚNICA prueba de que hay alguien trabajando: sin avisos recientes la
   * pantalla no puede afirmar "sigue trabajando".
   */
  ultimoAvisoAt?: number | null;
}

type ExtractMeta = {
  sectionDone: number;
  sectionTotal: number;
  readDone?: number;
  readTotal?: number;
};

function parseExtractMeta(message: string | null | undefined): ExtractMeta | null {
  if (!message) return null;
  const m = message.match(
    /secci[oó]n\s+(\d+)\s+de\s+(\d+)(?:\s*\(\s*lectura\s+(\d+)\s*\/\s*(\d+)\s*\))?/i,
  );
  if (!m) return null;
  const sectionDone = Number(m[1]);
  const sectionTotal = Number(m[2]);
  if (!Number.isFinite(sectionDone) || !Number.isFinite(sectionTotal) || sectionTotal <= 0) {
    return null;
  }
  const readDone = m[3] != null ? Number(m[3]) : undefined;
  const readTotal = m[4] != null ? Number(m[4]) : undefined;
  return {
    sectionDone,
    sectionTotal,
    readDone: Number.isFinite(readDone as number) ? readDone : undefined,
    readTotal: Number.isFinite(readTotal as number) ? readTotal : undefined,
  };
}

/**
 * 1 rojo → 2 amarillo → 3 verde.
 * Sin "sección X de Y" (Excel algorítmico o doc de un solo trozo) NO inventamos
 * avance: o estamos preparando (1) o la IA ya arrancó (2).
 */
function extractColorStage(
  message: string | null | undefined,
  meta: ExtractMeta | null,
): 1 | 2 | 3 {
  if (meta) {
    if (meta.readTotal != null && meta.readDone != null && meta.readTotal > 1) {
      return meta.readDone <= 1 ? 2 : 3;
    }
    // Un solo trozo: al reportar sección 1/1 ya es la pasada final.
    if (meta.sectionTotal <= 1) {
      return meta.sectionDone >= meta.sectionTotal ? 3 : 2;
    }
    const frac = meta.sectionDone / meta.sectionTotal;
    if (frac < 0.34) return 1;
    if (frac < 0.67) return 2;
    return 3;
  }
  const msg = (message ?? '').toLowerCase();
  if (
    msg.includes('inteligencia artificial')
    || msg.includes('analizando el documento')
    || msg.includes('documento extenso')
  ) {
    return 2;
  }
  return 1;
}

/** "(12/19)" al final del mensaje de pricing (embeddings / comparar / zona gris). */
function parsePricingCount(message: string | null | undefined): { done: number; total: number } | null {
  if (!message) return null;
  const m = message.match(/\((\d+)\s*\/\s*(\d+)\)\s*$/);
  if (!m) return null;
  const done = Number(m[1]);
  const total = Number(m[2]);
  if (!Number.isFinite(done) || !Number.isFinite(total) || total <= 0) return null;
  return { done, total };
}

/** mm:ss para el reloj del paso largo. */
function mmss(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Cuántos segundos lleva el avance sin moverse.
 *
 * Mientras un paso largo trabaja —leer el documento, validar una tanda, armar el
 * archivo final— no hay nada nuevo que contar, y un indicador quieto es
 * indistinguible de un sistema colgado. Esto NO mueve ningún número: solo mide el
 * silencio para poder decirlo con letras y con el reloj.
 *
 * `firma` es lo que cuenta como novedad (el número y el texto del worker). El worker
 * repite el último estado cada ~5 s, así que si la conexión se cortara el reloj
 * seguiría subiendo, que es exactamente lo que hay que mostrar.
 */
function useQuietoDesde(firma: string): number {
  const desde = useRef({ firma, at: Date.now() });
  const [, tick] = useState(0);
  if (desde.current.firma !== firma) desde.current = { firma, at: Date.now() };
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, Math.floor((Date.now() - desde.current.at) / 1000));
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Sin novedades del proceso por más de esto, ya no se puede afirmar que trabaja. */
const SIN_CONTACTO_SEG = 30;
/** Una espera en la fila más larga que esto ya no es normal: se avisa. */
const ESPERA_RARA_SEG = 90;

export function QuoteBuildingAnimation({
  stage,
  stageLabel,
  message,
  pct,
  processed,
  total,
  priced,
  etaMs,
  extra,
  enCola = false,
  esperandoDesde = null,
  ultimoAvisoAt = null,
}: QuoteBuildingAnimationProps) {
  const styles = useStyles();
  const eta = formatEta(etaMs);
  const meta = parseExtractMeta(message);
  const pricingCount = parsePricingCount(message);
  // En la fila NO se lee nada todavía: la rama de lectura queda fuera.
  const isExtract = !enCola && (stage === 'extract' || stage === 'queued');
  // En pricing el worker a veces deja conceptsProcessed=0 (embeddings / zona
  // gris) aunque el mensaje ya traiga (X/Y) real — preferir el del mensaje.
  const barDone = !isExtract
    ? (pricingCount?.done ?? processed)
    : processed;
  const barTotal = !isExtract
    ? (pricingCount?.total ?? total)
    : total;
  const hasCount =
    barTotal != null && barTotal > 0 && barDone != null && Number.isFinite(barDone);
  // Preparación (embeddings): sin (X/Y) aún → barra en movimiento, no 0/N quieto.
  const preparingPricing =
    !isExtract
    && !pricingCount
    && (/preparando/i.test(message ?? '') || (processed === 0 && (total ?? 0) > 0));
  const useConceptBar = !isExtract && hasCount && !preparingPricing;

  const colorStage = extractColorStage(message, meta);
  // Color de la marca en movimiento (sutil): en pricing azul; durante la lectura
  // avanza de suave a verde. NO se expone como "Etapa X/3" — es solo el color.
  const barColor = !isExtract ? STAGE_COLORS[1] : STAGE_COLORS[colorStage];

  const conceptBarPct = useConceptBar
    ? Math.min(100, Math.max(0, Math.round((barDone! / barTotal!) * 100)))
    : 0;

  // Lectura del documento (extracción): UNA sola frase clara de qué está pasando,
  // sin contadores internos (sección / lectura / etapa) que confundían — el
  // usuario no debe descifrar "sección 4 de 4 (lectura 2/2)" ni "Etapa 3/3".
  // Escaneo: el mensaje del worker trae "páginas del escaneo: N de M". Se muestra
  // tal cual, porque ahí sí hay avance real que contar — es lo único que el
  // usuario puede mirar durante los minutos que toma leer un documento por imagen.
  const escaneo = /p[áa]ginas del escaneo/i.test(message ?? '');
  const readingLine = escaneo
    ? (message as string)
    : colorStage === 1
      ? 'Preparando la lectura de tu documento…'
      : colorStage === 2
        ? 'Leyendo tu documento con inteligencia artificial. Si es extenso, puede tardar un par de minutos.'
        : 'Ya casi: terminando de leer tu documento.';

  const aria = enCola
    ? `${stageLabel}: en la fila, todavía no empieza`
    : useConceptBar
      ? `${stageLabel}: ${barDone} de ${barTotal} conceptos`
      : isExtract
        ? readingLine
        : stageLabel;

  // Paso largo: si en 15 s no se movió ni el número ni el texto, se dice con letras
  // y con el reloj. 15 s y no menos porque un concepto puede tardar eso en resolverse
  // y el aviso no debe aparecer entre uno y el siguiente.
  // (Este hook también hace que el componente se vuelva a pintar cada segundo, de lo
  //  que dependen los relojes de abajo.)
  const quietoSec = useQuietoDesde(`${barDone ?? ''}|${barTotal ?? ''}|${pct ?? ''}|${message ?? ''}`);
  const ahora = Date.now();
  // Piso del reloj de contacto: si la pantalla se abrió en medio de una corrida,
  // todavía no llegó ningún aviso y eso no es motivo para alarmar.
  const montadoAt = useRef(ahora);
  const sinContactoSec = Math.max(0, Math.floor((ahora - (ultimoAvisoAt ?? montadoAt.current)) / 1000));
  /**
   * "Sigue trabajando" es una AFIRMACIÓN, y solo se hace con pruebas: un aviso real
   * del proceso, y reciente (el worker repite el último estado cada ~5 s). Sin ningún
   * aviso todavía la pantalla se CALLA, y si los avisos se cortaron lo dice. Nunca
   * hacer compañía con un mensaje falso: en la fila no hay nadie trabajando, y con el
   * worker caído tampoco.
   */
  const hayContacto = !enCola && ultimoAvisoAt != null && sinContactoSec < SIN_CONTACTO_SEG;
  /** Sin una sola señal todavía: no se afirma nada hasta que la espera se vuelve rara. */
  const callarPorAhora = !enCola && ultimoAvisoAt == null && sinContactoSec < SIN_CONTACTO_SEG;
  const esperaSec = esperandoDesde != null
    ? Math.max(0, Math.floor((ahora - esperandoDesde) / 1000))
    : 0;

  const lineaLatido = enCola ? (
    esperaSec >= ESPERA_RARA_SEG ? (
      <div className={styles.aviso}>
        <span>
          Lleva {mmss(esperaSec)} en la fila y todavía no empieza. Puede que algo no esté
          bien de nuestro lado: vuelve a intentarlo en unos minutos y, si sigue igual, avísanos.
        </span>
      </div>
    ) : esperandoDesde != null ? (
      <div className={styles.latido}>
        <span>En la fila desde hace {mmss(esperaSec)}.</span>
      </div>
    ) : null
  ) : hayContacto ? (
    quietoSec >= 15 ? (
      <div className={styles.latido}>
        <span aria-hidden className={styles.latidoPunto} />
        <span>Sigue trabajando en este paso ({mmss(quietoSec)}). Los pasos largos pueden tardar varios minutos.</span>
      </div>
    ) : null
  ) : callarPorAhora ? null : (
    <div className={styles.aviso}>
      <span>
        Hace {mmss(sinContactoSec)} que no recibimos novedades de este proceso. Puede haberse
        interrumpido: espera un momento y, si no se mueve, vuelve a intentarlo.
      </span>
    </div>
  );

  return (
    <div className={styles.wrap} role="status" aria-label={aria} aria-busy={!enCola}>
      {enCola ? (
        <>
          {/* Fila de espera: se dice que espera, y NO se muestra nada en movimiento
              — no hay avance que mostrar porque nadie tomó el trabajo todavía. */}
          <div className={styles.row}>
            <div className={styles.textCol}>
              <span className={styles.title}>{stageLabel}</span>
              <span className={styles.sub}>
                Tu documento está en la fila. Empieza en cuanto se libere un lugar.
              </span>
            </div>
          </div>
          <div className={styles.barTrackQuieto} />
        </>
      ) : useConceptBar ? (
        <>
          <div className={styles.row}>
            <div className={styles.textCol}>
              <span className={styles.title}>{stageLabel}</span>
              {message && message !== stageLabel && (
                <span className={styles.sub} title={message}>{message}</span>
              )}
            </div>
            <span className={styles.count}>{barDone} / {barTotal}</span>
          </div>
          <div className={styles.barTrack}>
            <i
              className={styles.barFill}
              style={{ width: `${conceptBarPct}%`, background: 'var(--accent)' }}
            />
          </div>
          <div className={styles.metaRow}>
            {priced != null && (
              <span>
                con precio <span className={styles.metaNum}>{priced}</span>
              </span>
            )}
            {extra}
            {eta && (
              <span className={styles.eta}><ClockIcon /> termina en {eta}</span>
            )}
          </div>
        </>
      ) : isExtract ? (
        <>
          {/* Lectura del documento: UNA sola frase clara, sin contadores internos
              (nada de "sección 4 de 4", "lectura 2/2", "Etapa 3/3" ni "2/2"). */}
          <div className={styles.row}>
            <Spinner size="tiny" />
            <div className={styles.textCol}>
              <span className={styles.title}>{stageLabel}</span>
              <span className={styles.sub}>{readingLine}</span>
            </div>
          </div>
          <div
            className={styles.barTrackMoving}
            style={{
              backgroundImage: `linear-gradient(90deg, transparent 0%, ${barColor} 40%, ${barColor} 60%, transparent 100%)`,
            }}
          />
          {(eta || extra) && (
            <div className={styles.metaRow}>
              {extra}
              {eta && (
                <span className={styles.eta}><ClockIcon /> termina en {eta}</span>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className={styles.row}>
            <Spinner size="tiny" />
            <div className={styles.textCol}>
              <span className={styles.title}>{stageLabel}</span>
              {message && (
                <span className={styles.sub} title={message}>{message}</span>
              )}
            </div>
          </div>
          <div
            className={styles.barTrackMoving}
            style={{
              backgroundImage: `linear-gradient(90deg, transparent 0%, ${barColor} 40%, ${barColor} 60%, transparent 100%)`,
            }}
          />
          {(eta || extra) && (
            <div className={styles.metaRow}>
              {extra}
              {eta && (
                <span className={styles.eta}><ClockIcon /> termina en {eta}</span>
              )}
            </div>
          )}
        </>
      )}
      {lineaLatido}
    </div>
  );
}
